import React, { useState, useEffect, useRef, useCallback } from 'react';
import ResultPage from '../../Components/Games/ResultPage.jsx';
import StartGame from '../../Components/Games/StartGame.jsx';
import GameIntroScreen from '../../Components/Games/GameIntroScreen.jsx';
import './FocusTap.css';
import focusTapIcon from '../../assets/img/icon-Focus-Tap.png';
import overlay from '../../assets/img/overlay.png';
import TopBar from '../../Components/Games/TopBar.jsx';
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

// ─── GAME CONFIGURATION ─────────────────────────────────────────────────────
const GAME_DURATION     = 60;
const BASE_DURATION     = 1500;
const MIN_DURATION      = 600;
const TARGET_PROBABILITY = 0.7;
const FAST_THRESHOLD    = 800; // ms — reactions under this count as "Fast"

const TARGETS     = ['🍓', '🍏', '🍇', '🍉'];
const DISTRACTORS = ['💣', '🛑', '🧱', '🦟'];
const MOTIVATION  = ['Nice!', 'Sharp!', 'Keep it up!', 'Focus!', 'Great!', 'On fire!'];

// ─── TUTORIAL STEPS ─────────────────────────────────────────────────────────
const TUTORIAL_STEPS = [
  { icon: '🍓', title: 'TAP',        desc: 'When you see a fruit',   color: '#34d399' },
  { icon: '💣', title: "DON'T TAP",  desc: 'When you see a hazard',  color: '#f87171' },
  { icon: '🎯', title: 'Focus',      desc: 'Selective Attention'                      },
  { icon: '⚡', title: 'Control',    desc: 'Impulse Control'                           },
];

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
const FocusTap = () => {
  useGameBackNavigation();
  // Navigation: START_LOGO → HEALTH_BENEFITS → TUTORIAL → GAME → GAMEOVER
  const [phase, setPhase] = useState('START_LOGO');

  // Game state
  const [timeLeft,   setTimeLeft]   = useState(GAME_DURATION);
  const [score,      setScore]      = useState(0);
  const [stimulus,   setStimulus]   = useState(null);
  const [isTarget,   setIsTarget]   = useState(false);
  const [feedback,   setFeedback]   = useState(null);   // { type, text, delta }
  const [tutStep,    setTutStep]    = useState(0);

  // Stats
  const [hits,          setHits]          = useState(0);
  const [misses,        setMisses]        = useState(0);
  const [falseAlarms,   setFalseAlarms]   = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak,    setBestStreak]    = useState(0);
  const [fastResponses, setFastResponses] = useState(0);

  // API / backend state
  const [bestScore,            setBestScore]            = useState(() => parseInt(localStorage.getItem('ft_bestScore')) || 0);
  const [goqiiBest,            setGoqiiBest]            = useState(0);
  const [cid,                  setCid]                  = useState('');
  const [weightages,           setWeightages]           = useState({
    attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1,
    fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0,
    correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1,
  });
  const [configError,           setConfigError]          = useState(false);
  const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
  const [backendSkill,          setBackendSkill]          = useState('');

  // Floating score popups  [ { id, delta, x, y } ]
  const [floatScores, setFloatScores] = useState([]);

  // Refs
  const timerRef           = useRef(null);
  const loopRef            = useRef(null);
  const timeoutRef         = useRef(null);
  const stimulusStartTime  = useRef(null);
  const stateRef           = useRef({ isTarget: false, phase: 'START_LOGO', score: 0 });
  const lastInsertedIdRef  = useRef(null);

  // Keep stateRef in sync (used inside timer/timeout callbacks to read current phase)
  useEffect(() => {
    stateRef.current = { isTarget, phase, score };
  }, [isTarget, phase, score]);

  // Cleanup on unmount
  useEffect(() => () => clearAllTimers(), []);

  // ─── FETCH GAME DETAILS ON MOUNT ───────────────────────────────────────────
  const fetchGameConfig = () => {
    setConfigError(false);
    const { encryptedId, gid } = getUrlParams();
    lifestyleApi.fetchGameDetails(encryptedId, gid)
      .then(res => {
        if (res.data?.code === 200) {
          const d = res.data.data;
          setBestScore(d.bestScore);
          setGoqiiBest(d.goqiiBest);
          setWeightages({
            attemptWeightage:          d.attemptWeightage          ?? 1,
            accuracyWeightage:         d.accuracyWeightage         ?? 1,
            streakWeightage:           d.streakWeightage           ?? 1,
            fastWeightage:             d.fastWeightage             ?? 1,
            scoreWeightage:            d.scoreWeightage            ?? 1,
            baseQuestionsAttempt:      d.baseQuestionsAttempt      || 0,
            correctMinQuestion:        d.correctMinQuestion        || 1,
            streakMinQuestion:         d.streakMinQuestion         || 1,
            fastResponseMinQuestion:   d.fastResponseMinQuestion   || 1,
            scorePoints:               d.scorePoints               || 1,
          });
        } else {
          setConfigError(true);
        }
      })
      .catch(err => {
        console.error('fetchGameDetails failed', err);
        setConfigError(true);
      });
  };

  useEffect(() => {
    fetchGameConfig();
  }, []);

  // ─── AUTO-SAVE WHEN GAMEOVER  (matches other games' pattern exactly) ───────
  useEffect(() => {
    if (phase === 'GAMEOVER') {
      saveFinalGameScore();
    }
  }, [phase]);

  // ─── HELPERS ────────────────────────────────────────────────────────────────
  const clearAllTimers = () => {
    clearInterval(timerRef.current);
    clearTimeout(loopRef.current);
    clearTimeout(timeoutRef.current);
  };

  const addFloatScore = (delta) => {
    const id = Date.now() + Math.random();
    setFloatScores(prev => [...prev, { id, delta }]);
    setTimeout(() => setFloatScores(prev => prev.filter(f => f.id !== id)), 900);
  };

  // ─── START GAME ─────────────────────────────────────────────────────────────
  const startGame = async () => {
    const { encryptedId, gid } = getUrlParams();

    // Reset everything before API call (same order as other games)
    lastInsertedIdRef.current = null;
    setCid('');
    setScore(0);
    setTimeLeft(GAME_DURATION);
    setHits(0);
    setMisses(0);
    setFalseAlarms(0);
    setCurrentStreak(0);
    setBestStreak(0);
    setFastResponses(0);
    setFeedback(null);
    setStimulus(null);
    setFloatScores([]);
    setBackendCognitiveScore(null);
    setBackendSkill('');

    try {
      const res = await lifestyleApi.initialGameScore(encryptedId, gid);
      if (res.data?.code === 200) {
        const data = res.data.data;
        setCid(data.cid);
        lastInsertedIdRef.current = data.lastInsertedId;
      }
    } catch (err) {
      console.error('initialGameScore failed', err);
    }

    setPhase('GAME');

    // Start countdown timer — matches other games: set GAMEOVER from inside updater
    clearAllTimers();
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearTimeout(loopRef.current);
          clearTimeout(timeoutRef.current);
          setPhase('GAMEOVER');   // ← same pattern as MemoryBlitz / ShapeShift
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    scheduleNextStimulus(1000);
  };

  // ─── SAVE FINAL SCORE (matches other games exactly) ─────────────────────────
  const saveFinalGameScore = async () => {
    const { encryptedId, gid } = getUrlParams();
    const goqiiUserId   = encryptedId;
    const wrong         = misses + falseAlarms;
    const totalQuestions = hits + wrong;

    const payload = {
      goqiiUserId,
      gid,
      totalQuestions,
      correctAnswers:  hits,
      wrongAnswers:    wrong,
      longestStreak:   bestStreak,
      fastResponse:    fastResponses,
      score,
      cid,
      lastInsertedId:  lastInsertedIdRef.current,
    };

    try {
      const res = await lifestyleApi.saveGameScore(
        payload.goqiiUserId,
        payload.gid,
        payload.totalQuestions,
        payload.correctAnswers,
        payload.wrongAnswers,
        payload.longestStreak,
        payload.fastResponse,
        payload.score,
        payload.cid,
        payload.lastInsertedId
      );

      if (res?.data?.data?.cognitiveScore !== undefined) {
        setBackendCognitiveScore(res.data.data.cognitiveScore);
      }
      if (res?.data?.data?.skill) {
        setBackendSkill(res.data.data.skill);
      }
    } catch (err) {
      console.error('saveGameScore failed', err);
    }
  };

  // ─── STIMULUS LOOP ──────────────────────────────────────────────────────────
  const scheduleNextStimulus = (delay) => {
    loopRef.current = setTimeout(triggerGameLoop, delay);
  };

  const triggerGameLoop = () => {
    if (stateRef.current.phase !== 'GAME') return;

    const isTargetItem = Math.random() < TARGET_PROBABILITY;
    const list = isTargetItem ? TARGETS : DISTRACTORS;

    setStimulus(list[Math.floor(Math.random() * list.length)]);
    setIsTarget(isTargetItem);
    setFeedback(null);
    stimulusStartTime.current = Date.now();

    const elapsed  = GAME_DURATION - (stateRef.current.phase === 'GAME' ? 0 : 0);
    const progress = 0; // approximate — timer manages timing
    const duration = Math.max(
      MIN_DURATION,
      BASE_DURATION - (progress * (BASE_DURATION - MIN_DURATION))
    );

    timeoutRef.current = setTimeout(() => handleTimeout(isTargetItem), duration);
  };

  const handleTimeout = (wasTarget) => {
    if (stateRef.current.phase !== 'GAME') return;
    setStimulus(null);
    if (wasTarget) {
      // Missed target = wrong action: same penalty & streak reset as a false alarm.
      const penalty = weightages.scorePoints / 2;
      setScore(s => Math.max(0, s - penalty));
      setMisses(m => m + 1);
      setCurrentStreak(0);
      setFeedback({ type: 'MISS', text: 'Missed!' });
    }
    scheduleNextStimulus(Math.random() * 500 + 500);
  };

  // ─── TAP HANDLER ────────────────────────────────────────────────────────────
  const handleTap = (e) => {
    if (e) e.preventDefault();
    if (stateRef.current.phase !== 'GAME' || !stimulus) return;

    const reactionTime = Date.now() - stimulusStartTime.current;
    clearTimeout(timeoutRef.current);
    setStimulus(null);

    if (stateRef.current.isTarget) {
      // ✅ CORRECT TAP
      const isFast = reactionTime < FAST_THRESHOLD;
      const fastBonus = isFast ? weightages.fastWeightage * 10 : 0;
      const streakBonus = weightages.streakWeightage * 10 * currentStreak; // streak BEFORE this hit
      const gained = weightages.scorePoints + streakBonus + fastBonus;
      setScore(s => Math.max(0, s + gained));
      setHits(h => h + 1);
      addFloatScore(`+${gained}`);
      setFeedback({ type: 'GOOD', text: MOTIVATION[Math.floor(Math.random() * MOTIVATION.length)] });
      setCurrentStreak(prev => {
        const next = prev + 1;
        setBestStreak(b => (next > b ? next : b));
        return next;
      });
      if (isFast) setFastResponses(f => f + 1);
    } else {
      // ❌ WRONG TAP (false alarm)
      const penalty = weightages.scorePoints / 2;
      setScore(s => Math.max(0, s - penalty));
      setFalseAlarms(f => f + 1);
      addFloatScore(`-${penalty}`);
      setFeedback({ type: 'BAD', text: "Don't Tap!" });
      setCurrentStreak(0);
    }

    scheduleNextStimulus(Math.random() * 300 + 200);
  };

  // ─── BEST SCORE UPDATE ──────────────────────────────────────────────────────
  useEffect(() => {
    if (phase === 'GAMEOVER' && score > bestScore) {
      setBestScore(score);
      localStorage.setItem('ft_bestScore', score.toString());
    }
  }, [phase]);

  // ─── QUOTE ──────────────────────────────────────────────────────────────────
  const getQuote = () => {
    if (backendCognitiveScore >= 90) return 'Elite focus! You react fast and ignore distractions like a pro.';
    if (backendCognitiveScore >= 75) return 'Sharp attention! Your brain filters noise effectively.';
    if (backendCognitiveScore >= 60) return 'Good control. Your focus is getting stronger.';
    if (backendCognitiveScore >= 40) return 'Nice effort. Train more to improve reaction accuracy.';
    return 'Every tap trains your mind. Stay focused and try again!';
  };

  // ─── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="ft-root">

      {configError && (
        <div className="api-error-overlay">
          <div className="api-error-card">
            <div className="api-error-title">Something went wrong</div>
            <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
            <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
          </div>
        </div>
      )}

      {/* ── 1. INTRO ── */}
      {phase === 'START_LOGO' && (
        <GameIntroScreen
          backgroundImage={overlay}
          gameLogo={focusTapIcon}
          title="Focus Tap"
          onStart={() => setPhase('HEALTH_BENEFITS')}
        />
      )}

      {/* ── 2. HEALTH BENEFITS / START ── */}
      {phase === 'HEALTH_BENEFITS' && (
        <StartGame
          backgroundImage={overlay}
          hbHeading="Selective Attention"
          hbDescription="Train your brain to instantly identify targets and suppress impulse reactions to distractors."
          yourBest={bestScore}
          goqiiBest={goqiiBest}
          onStart={() => setPhase('TUTORIAL')}
        />
      )}

      {/* ── 3. TUTORIAL ── */}
      {phase === 'TUTORIAL' && (
        <div className="ft-tutorial">
          <div className="ft-tut-header">
            <span className="ft-tut-badge">HOW TO PLAY</span>
            <button className="ft-tut-skip" onClick={startGame}>Skip ⏩</button>
          </div>

          <div className="ft-tut-card">
            <div className="ft-tut-icon">{TUTORIAL_STEPS[tutStep].icon}</div>
            <h3 className="ft-tut-title" style={{ color: TUTORIAL_STEPS[tutStep].color || '#94a3b8' }}>
              {TUTORIAL_STEPS[tutStep].title}
            </h3>
            <p className="ft-tut-desc">{TUTORIAL_STEPS[tutStep].desc}</p>

            {tutStep < TUTORIAL_STEPS.length - 1 ? (
              <button className="ft-btn-primary" onClick={() => setTutStep(t => t + 1)}>
                Next →
              </button>
            ) : (
              <button className="ft-btn-primary ft-btn-green" onClick={startGame}>
                Start Game ▶
              </button>
            )}
          </div>

          {/* Dot indicators */}
          <div className="ft-tut-dots">
            {TUTORIAL_STEPS.map((_, i) => (
              <span key={i} className={`ft-tut-dot ${i === tutStep ? 'active' : ''}`} />
            ))}
          </div>
        </div>
      )}

      {/* ── 4. GAME ── */}
      {phase === 'GAME' && (
        <div className="ft-game-wrapper">
          <TopBar score={score} timeLeft={timeLeft} />

          {/* Streak badge */}
          {currentStreak >= 2 && (
            <div className="ft-streak-badge">
              🔥 {currentStreak}
            </div>
          )}

          {/* Floating score deltas */}
          {floatScores.map(f => (
            <div
              key={f.id}
              className={`ft-float-score ${f.delta.startsWith('+') ? 'ft-float-pos' : 'ft-float-neg'}`}
            >
              {f.delta}
            </div>
          ))}

          {/* Play area */}
          <div
            className={`ft-play-area
              ${stimulus ? 'ft-play-active' : ''}
              ${feedback?.type === 'GOOD' ? 'ft-play-good' : ''}
              ${feedback?.type === 'BAD'  ? 'ft-play-bad'  : ''}
              ${feedback?.type === 'MISS' ? 'ft-play-miss' : ''}
            `}
            onMouseDown={handleTap}
            onTouchStart={handleTap}
          >
            {/* Outer pulse ring */}
            <div className={`ft-ring ft-ring-outer ${!stimulus && !feedback ? 'ft-ring-pulse' : ''}`} />
            {/* Inner ring */}
            <div className={`ft-ring ft-ring-inner`} />

            {/* Stimulus emoji */}
            {stimulus && (
              <div className="ft-stimulus ft-anim-pop">{stimulus}</div>
            )}

            {/* Feedback overlay */}
            {feedback && !stimulus && (
              <div className={`ft-feedback ft-anim-pop ${feedback.type === 'GOOD' ? 'ft-feedback-good' : feedback.type === 'BAD' ? 'ft-feedback-bad' : 'ft-feedback-miss'}`}>
                {feedback.type === 'GOOD' && <span className="ft-feedback-icon">✅</span>}
                {feedback.type === 'BAD'  && <span className="ft-feedback-icon">❌</span>}
                {feedback.type === 'MISS' && <span className="ft-feedback-icon">⏱️</span>}
                <span className="ft-feedback-text">{feedback.text}</span>
              </div>
            )}

            {/* Idle hint */}
            {!stimulus && !feedback && (
              <div className="ft-idle-hint">
                <span className="ft-idle-dot" />
                <span className="ft-idle-dot" />
                <span className="ft-idle-dot" />
              </div>
            )}
          </div>

          {/* Legend row */}
          <div className="ft-legend">
            <div className="ft-legend-item ft-legend-tap">
              <span>🍓</span> <span>TAP</span>
            </div>
            <div className="ft-legend-divider" />
            <div className="ft-legend-item ft-legend-stop">
              <span>💣</span> <span>STOP</span>
            </div>
          </div>
        </div>
      )}

      {/* ── 5. RESULT (GAMEOVER) — same as other games ── */}
      {phase === 'GAMEOVER' && (
        <ResultPage
          visible={true}
          score={score}
          best={bestScore}
          correct={hits}
          wrong={misses + falseAlarms}
          longestStreak={bestStreak}
          fastResponses={fastResponses}
          cognitiveScore={backendCognitiveScore}
          domainLabel={backendSkill}
          onReplay={() => setPhase('HEALTH_BENEFITS')}
          onShare={() => { /* TODO: share */ }}
          quote={getQuote()}
        />
      )}
    </div>
  );
};

export default FocusTap;
